import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, LogOut, Loader2, RefreshCw } from "lucide-react";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const Admin = () => {
  const [state, setState] = useState("checking");
  const [enquiries, setEnquiries] = useState([]);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const loadEnquiries = async () => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setEnquiries(data);
      setState("ready");
    } catch (e) {
      if (e.response?.status === 401) setState("login");
      else setError("Could not load enquiries. Please refresh.");
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(() => loadEnquiries())
      .catch(() => setState("login"));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      await loadEnquiries();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setEnquiries([]);
    setState("login");
  };

  const [filter, setFilter] = useState("all");

  const patchEnquiry = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/enquiries/${id}`, patch, { withCredentials: true });
      setEnquiries((list) => list.map((e) => (e.id === id ? data : e)));
      return true;
    } catch {
      setError("Could not update the enquiry. Please refresh and try again.");
      return false;
    }
  };

  if (state === "checking") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-espresso pt-28">
        <Loader2 className="animate-spin text-gold" size={28} />
      </main>
    );
  }

  if (state === "login") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-espresso px-5 pt-28 pb-20">
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm rounded-sm border border-cream/10 bg-coffee/60 p-8"
        >
          <div className="mb-8 flex justify-center">
            <Logo compact />
          </div>
          <p className="mb-6 text-center font-display text-2xl text-cream">Owner Login</p>
          <div className="space-y-5">
            <input
              data-testid="admin-email-input"
              className="field-line"
              placeholder="Email"
              type="email"
              value={creds.email}
              onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))}
            />
            <input
              data-testid="admin-password-input"
              className="field-line"
              placeholder="Password"
              type="password"
              value={creds.password}
              onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-center text-sm text-red-300/90">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-submit-button"
            type="submit"
            disabled={busy}
            className="mt-7 w-full rounded-full bg-gold py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-espresso transition-all duration-300 hover:bg-cream disabled:opacity-50"
          >
            {busy ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-inbox-page" className="min-h-screen bg-espresso px-5 pb-24 pt-32 md:px-10">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-widest2 text-gold">Owner</p>
            <h1 className="mt-2 font-display text-3xl text-cream">Enquiry Inbox</h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={loadEnquiries}
              className="inline-flex items-center gap-2 rounded-full border border-cream/20 px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-cream/70 transition-colors hover:border-gold hover:text-gold"
            >
              <RefreshCw size={13} />
              Refresh
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-cream/20 px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-cream/70 transition-colors hover:border-red-400/60 hover:text-red-300"
            >
              <LogOut size={13} />
              Logout
            </button>
          </div>
        </div>

        <p data-testid="admin-enquiry-count" className="mt-4 text-sm text-cream/50">
          {enquiries.length} {enquiries.length === 1 ? "enquiry" : "enquiries"} received
        </p>

        {error && <p className="mt-6 text-sm text-red-300/90">{error}</p>}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
          <p data-testid="admin-enquiry-count" className="text-sm text-cream/50">
            {enquiries.length} {enquiries.length === 1 ? "enquiry" : "enquiries"} ·{" "}
            {enquiries.filter((e) => (e.status || "new") === "new").length} new
          </p>
          <div className="flex gap-2">
            {[
              ["all", "All"],
              ["new", "New"],
              ["contacted", "Contacted"],
            ].map(([key, label]) => (
              <button
                key={key}
                data-testid={`admin-filter-${key}`}
                onClick={() => setFilter(key)}
                className={`rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.15em] transition-all duration-300 ${
                  filter === key
                    ? "bg-gold text-espresso"
                    : "border border-cream/20 text-cream/60 hover:border-gold hover:text-gold"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 space-y-4">
          {enquiries.length === 0 && !error && (
            <p className="rounded-sm border border-cream/10 bg-coffee/40 p-10 text-center text-sm text-cream/50">
              No enquiries yet. Dealer and product enquiries will appear here.
            </p>
          )}
          {(filter === "all" ? enquiries : enquiries.filter((e) => (e.status || "new") === filter)).map(
            (eq, i) => (
              <EnquiryCard key={eq.id || i} eq={eq} index={i} onPatch={patchEnquiry} />
            )
          )}
        </div>
      </div>
    </main>
  );
};

const EnquiryCard = ({ eq, index, onPatch }) => {
  const [noteDraft, setNoteDraft] = useState(eq.note || "");
  const [saving, setSaving] = useState(false);
  const status = eq.status || "new";

  const saveNote = async () => {
    setSaving(true);
    await onPatch(eq.id, { note: noteDraft });
    setSaving(false);
  };

  return (
    <article
      data-testid={`admin-enquiry-card-${index + 1}`}
      className="rounded-sm border border-cream/10 bg-coffee/40 p-6 transition-colors duration-300 hover:border-gold/40"
    >
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${
            eq.enquiry_type === "dealer" ? "bg-gold/15 text-gold" : "bg-sage/30 text-sagelight"
          }`}
        >
          {eq.enquiry_type === "dealer" ? "Dealer" : "Product"}
        </span>
        <button
          data-testid={`admin-status-toggle-${index + 1}`}
          onClick={() => onPatch(eq.id, { status: status === "contacted" ? "new" : "contacted" })}
          className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.15em] transition-all duration-300 ${
            status === "contacted"
              ? "bg-sage/40 text-sagelight"
              : "bg-gold/15 text-gold hover:bg-gold hover:text-espresso"
          }`}
        >
          {status === "contacted" ? "Contacted" : "New"}
        </button>
        <h2 className="font-display text-xl text-cream">{eq.name}</h2>
        <span className="text-xs text-cream/40">
          {new Date(eq.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
        </span>
      </div>
      <div className="mt-4 grid gap-2 text-sm text-cream/70 sm:grid-cols-2">
        {eq.business_name && <p><span className="text-cream/40">Business:</span> {eq.business_name}</p>}
        {eq.city && <p><span className="text-cream/40">City:</span> {eq.city}</p>}
        {eq.business_type && <p><span className="text-cream/40">Type:</span> {eq.business_type}</p>}
        {eq.requirement && <p><span className="text-cream/40">Requirement:</span> {eq.requirement}</p>}
      </div>
      {eq.message && <p className="mt-3 text-sm italic text-cream/60">“{eq.message}”</p>}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <a
          data-testid={`admin-enquiry-call-${index + 1}`}
          href={`tel:${eq.phone}`}
          className="inline-flex items-center gap-2.5 rounded-full bg-gold/15 px-5 py-2.5 text-xs font-semibold tracking-[0.1em] text-gold transition-all duration-300 hover:bg-gold hover:text-espresso"
        >
          <Phone size={13} />
          {eq.phone}
        </a>
        <textarea
          data-testid={`admin-note-input-${index + 1}`}
          className="field-line min-w-[220px] flex-1"
          rows="1"
          placeholder="Private note (e.g. called, will visit Friday)"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
        />
        <button
          data-testid={`admin-note-save-${index + 1}`}
          onClick={saveNote}
          disabled={saving || (eq.note || "") === noteDraft}
          className="rounded-full border border-gold/60 px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:bg-gold hover:text-espresso disabled:opacity-40"
        >
          {saving ? "Saving..." : "Save Note"}
        </button>
      </div>
    </article>
  );
};

export default Admin;
